import React, { useEffect, useState } from 'react';
import { Animated, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { YStack } from 'tamagui';
import { useThemeColors } from '../hooks/useThemeColors';
import { colors } from '../theme/colors';
import { dropShadow } from '../theme/shadowStyles';

/** Default sidebar width. Matches hamburger menu and content panels. */
export const APP_SIDEBAR_WIDTH = 320;

/** Outer breathing room so the sidebar reads as a floating card. */
const SIDEBAR_MARGIN = 12;
const SIDEBAR_RADIUS = 24;

interface AppSidebarProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** @deprecated No longer needed — surface color comes from useThemeColors. Kept for callsite compat. */
  isDark?: boolean | undefined;
  width?: number | undefined;
}

/** Unified slide-in sidebar. Floats off the right edge as a rounded card. */
export function AppSidebar({ open, onClose, children, width = APP_SIDEBAR_WIDTH }: AppSidebarProps) {
  const insets = useSafeAreaInsets();
  const themeColors = useThemeColors();
  const hiddenOffset = width + SIDEBAR_MARGIN;
  const [slideAnim] = useState(() => new Animated.Value(hiddenOffset));
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const useNative = Platform.OS !== 'web';
    if (open) {
      setMounted(true);
      Animated.timing(slideAnim, { toValue: 0, duration: 220, useNativeDriver: useNative }).start();
    } else {
      Animated.timing(slideAnim, { toValue: hiddenOffset, duration: 200, useNativeDriver: useNative }).start(() => setMounted(false));
    }
  }, [open, slideAnim, hiddenOffset]);

  if (!mounted) return null;

  return (
    <View style={[StyleSheet.absoluteFill, { zIndex: 100 }]} pointerEvents="box-none">
      <Pressable style={styles.backdrop} onPress={onClose} />
      <Animated.View
        style={[
          styles.sidebar,
          {
            width,
            top: insets.top + SIDEBAR_MARGIN,
            bottom: insets.bottom + SIDEBAR_MARGIN,
            right: SIDEBAR_MARGIN,
            transform: [{ translateX: slideAnim }],
            backgroundColor: themeColors.surface,
            borderWidth: StyleSheet.hairlineWidth,
            borderColor: themeColors.borderLight,
          },
        ]}
      >
        <YStack flex={1} minHeight={0} padding="$4">{children}</YStack>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  backdrop: { ...StyleSheet.absoluteFill, backgroundColor: colors.overlayScrim },
  sidebar: {
    position: 'absolute',
    overflow: 'hidden',
    borderRadius: SIDEBAR_RADIUS,
    ...dropShadow({ offsetX: -2, offsetY: 4, radius: 16, opacity: 0.18, color: colors.shadowDefault, elevation: 12 }),
  },
});
